import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as TemplateActions from '../../ngrx/template/template.actions';
import * as fromTemplate from '../../ngrx/template/template.selectors';
import { take } from 'rxjs/operators';

export interface TabState {
  loading: boolean;
  failed: boolean;
  loaded: boolean;
}

/**
 * Service to manage tab loading states
 * Provided at root level to ensure a single shared instance
 */
@Injectable({
  providedIn: 'root'
})
export class TabService {
  // Unique identifier for this tab service instance
  private instanceId = `tabs_${Math.random().toString(36).substring(2, 9)}`;
  
  // State tracking maps
  private tabStatesMap = new Map<string, BehaviorSubject<TabState>>();
  private loadAttempts = new Map<string, number>();
  private readonly MAX_LOAD_ATTEMPTS = 3;
  private tabLoadTimeouts = new Map<string, any>();
  private readonly TIMEOUT_DURATION = 10000;
  private readonly TAB_PREFIX = 'tab/';
  private lastStateUpdateTime = new Map<string, number>(); // Track last update time per slug
  
  constructor(private store: Store<any>) {
  }
  
  // Get instance ID for debugging
  getInstanceId(): string {
    return this.instanceId;
  }
  
  // Create a scoped slug key to prevent collisions between multiple instances
  private getScopedKey(slug: string): string {
    return `${this.instanceId}:${slug}`;
  }
  
  // Get or create a TabState subject for a slug
  getTabStateSubject(slug: string): BehaviorSubject<TabState> {
    const key = this.getScopedKey(slug);
    
    if (!this.tabStatesMap.has(key)) {
      this.tabStatesMap.set(key, new BehaviorSubject<TabState>({
        loading: false,
        failed: false,
        loaded: false
      }));
      
      // Check if data is already loaded in store
      this.updateTabLoadedState(slug);
    }
    return this.tabStatesMap.get(key)!;
  }
  
  // Update a tab's state
  updateTabState(slug: string, state: Partial<TabState>): void {
    if (!slug) return;
    
    // Debounce rapid successive updates for the same slug
    const now = Date.now();
    const lastUpdate = this.lastStateUpdateTime.get(slug) || 0;
    const timeSinceLastUpdate = now - lastUpdate;
    
    // If we just updated this slug within 50ms, skip unless it's a significant change
    if (timeSinceLastUpdate < 50) {
      const subject = this.getTabStateSubject(slug);
      const currentState = subject.getValue();
      
      // Only allow updates if there's a significant state change (loading state changes)
      const isSignificantChange = (
        (state.loading !== undefined && state.loading !== currentState.loading) ||
        (state.failed !== undefined && state.failed !== currentState.failed)
      );
      
      if (!isSignificantChange) {
        return; // Skip rapid successive non-significant updates
      }
    }
    
    const subject = this.getTabStateSubject(slug);
    const currentState = subject.getValue();
    
    // Deep comparison to prevent unnecessary state updates
    let hasChanges = false;
    
    if (state.loading !== undefined && state.loading !== currentState.loading) {
      hasChanges = true;
    }
    
    if (state.failed !== undefined && state.failed !== currentState.failed) {
      hasChanges = true;
    }
    
    if (state.loaded !== undefined && state.loaded !== currentState.loaded) {
      hasChanges = true;
    }
    
    // Only update if there's an actual change and prevent cycling
    if (hasChanges) {
      // CRITICAL FIX: Prevent cycling from loaded->loading->loaded
      const newState = { ...currentState, ...state };
      
      // Block cycling: don't set loading=true if already loaded
      if (newState.loading === true && currentState.loaded === true && !state.failed) {
        return;
      }
      
      subject.next(newState);
      this.lastStateUpdateTime.set(slug, now);
    }
    // Remove the spam log for identical state updates - this is normal behavior
  }
  
  // Get observable for a tab's state
  getTabState$(slug: string): Observable<TabState> {
    return this.getTabStateSubject(slug).asObservable();
  }
  
  // Mark loaded tabs as loaded in their state
  private updateTabLoadedState(slug: string): void {
    if (!slug) return;
    
    try {
      this.store
        .select(fromTemplate.selectSlugData(slug))
        .pipe(take(1))
        .subscribe(data => {
          const isLoaded = !!data && !!data.loaded;
          if (isLoaded) {
            this.updateTabState(slug, { loaded: true, loading: false, failed: false });
          }
        });
    } catch (error) {
      console.error(`[TabService] Error checking loaded state for ${slug}:`, error);
    }
  }
  
  // Check if data is already loaded in store
  isDataLoaded(slug: string): boolean {
    if (!slug) return false;
    
    let result = false;
    
    try {
      this.store
        .select(fromTemplate.selectSlugData(slug))
        .pipe(take(1))
        .subscribe(data => {
          result = !!data && !!data.loaded;
          
          // If we found that data is loaded, update the tab state
          if (result) {
            this.updateTabState(slug, { loaded: true, loading: false, failed: false });
          }
        });
    } catch (error) {
      console.error(`[TabService] Error checking if data is loaded for ${slug}:`, error);
      return false;
    }
      
    return result;
  }
  
  // Check if tab content is loaded and log appropriately
  checkLoaded(slug: string): boolean {
    if (!slug) return false;
    
    let storeResult = false;
    
    try {
      // First check the store directly
      this.store
        .select(fromTemplate.selectSlugData(slug))
        .pipe(take(1))
        .subscribe(data => {
          storeResult = !!data && !!data.loaded;
        });
    } catch (error) {
      console.error(`[TabService] Error checking store data for ${slug}:`, error);
    }
    
    // Then check our local state
    const tabState = this.getTabStateSubject(slug).getValue();
    const isLoaded = tabState.loaded || storeResult;

    // Only log if there's a mismatch or if we're in debug mode
    if (storeResult && !tabState.loaded) {
      this.updateTabState(slug, { loaded: true, loading: false, failed: false });
    }
    
    return isLoaded;
  }
  
  // Simple debug mode check to reduce console spam
  private isDebugMode(): boolean {
    // Could be controlled via environment variable or setting
    return true; // Enable for debugging tab issues
  }
  
  // Load tab content
  loadTabContent(slug: string, forceReload = false): void {
    if (!slug) return;
    
    // CRITICAL FIX: Check if this tab is already loaded and avoid cycling
    const currentState = this.getTabStateSubject(slug).getValue();
    if (currentState.loaded && !forceReload) {
      return;
    }
    
    // Throttle rapid successive calls - landscape mode fix
    const now = Date.now();
    const lastCallTime = this.lastStateUpdateTime.get(slug) || 0;
    if (now - lastCallTime < 500) { // Increased to 500ms
      return;
    }
    
    
    const key = this.getScopedKey(slug);
    
    // Get current tab state up front to avoid race conditions
    const currentTabState = this.getTabStateSubject(slug).getValue();
    
    // If forcing reload, reset all state immediately
    if (forceReload) {
      this.updateTabState(slug, { loading: true, failed: false, loaded: false });
      this.loadAttempts.set(key, 0); // Reset attempts
    }
    
    // Check store state first - but be more careful about early returns
    let shouldProceedWithLoad = true;
    
    try {
      this.store
        .select(fromTemplate.selectSlugData(slug))
        .pipe(take(1))
        .subscribe(data => {
          const storeHasData = !!data && !!data.loaded;
          
          // Only skip if we have confirmed store data AND not forcing reload AND our local state agrees
          if (storeHasData && !forceReload && currentTabState.loaded) {
            this.updateTabState(slug, { loading: false, failed: false, loaded: true });
            shouldProceedWithLoad = false;
            return;
          }
          
          // If we have store data but local state disagrees, sync the state but continue loading to be safe
          if (storeHasData && !forceReload) {
            this.updateTabState(slug, { loading: false, failed: false, loaded: true });
            shouldProceedWithLoad = false;
            return;
          }
          
          // Check for too many attempts ONLY if not forcing reload
          const attempts = this.loadAttempts.get(key) || 0;
          if (attempts >= this.MAX_LOAD_ATTEMPTS && !forceReload) {
            this.updateTabState(slug, { loading: false, failed: true, loaded: false });
            shouldProceedWithLoad = false;
            return;
          }
          
          // Additional safety check: don't start loading if already loading (unless forced)
          if (currentTabState.loading && !forceReload) {
            shouldProceedWithLoad = false;
            return;
          }
          
          // If we get here, proceed with the load
          if (shouldProceedWithLoad) {
            this.performActualLoad(slug, key, forceReload);
          }
        });
    } catch (error) {
      console.error(`[TabService] Error in store check for ${slug}:`, error);
      // On error, proceed with load to be safe
      if (shouldProceedWithLoad) {
        this.performActualLoad(slug, key, forceReload);
      }
    }
  }
  
  // Extracted method to perform the actual loading logic
  private performActualLoad(slug: string, key: string, forceReload: boolean): void {
    
    // Mark as loading
    this.updateTabState(slug, { loading: true, failed: false });
    
    // Update attempt count
    const attempts = this.loadAttempts.get(key) || 0;
    if (!forceReload) {
      this.loadAttempts.set(key, attempts + 1);
    }
    
    try {
      // Dispatch the action to load data
      this.store.dispatch(TemplateActions.loadPageDataBySlug({ slug }));
      
      // Clear any existing timeout
      if (this.tabLoadTimeouts.has(key)) {
        clearTimeout(this.tabLoadTimeouts.get(key));
        this.tabLoadTimeouts.delete(key);
      }
      
      // Set up success monitoring with improved logic
      const monitorTimeout = setTimeout(() => {
        this.store.select(fromTemplate.selectSlugData(slug))
          .pipe(take(1))
          .subscribe(updatedData => {
            // CRITICAL FIX: Only mark as loaded if we have complete data
            if (updatedData && updatedData.loaded && updatedData.customSlug) {
              this.updateTabState(slug, { loading: false, failed: false, loaded: true });
              
              // Clear timeout
              if (this.tabLoadTimeouts.has(key)) {
                clearTimeout(this.tabLoadTimeouts.get(key));
                this.tabLoadTimeouts.delete(key);
              }
            } else {
              // Set up safety timeout for failure detection
              const safetyTimeout = setTimeout(() => {
                const currentState = this.getTabStateSubject(slug).getValue();
                if (currentState.loading) {
                  this.handleLoadFailure(slug);
                  
                  if (this.tabLoadTimeouts.has(key)) {
                    this.tabLoadTimeouts.delete(key);
                  }
                }
              }, this.TIMEOUT_DURATION - 200); // Slightly shorter than main timeout
              
              this.tabLoadTimeouts.set(key, safetyTimeout);
            }
          });
      }, 150); // Give a bit more time for the action to complete
      
      this.tabLoadTimeouts.set(key, monitorTimeout);
      
    } catch (error) {
      console.error(`[TabService] Error loading content for ${slug}:`, error);
      this.handleLoadFailure(slug);
    }
  }
  
  // Handle load failure
  handleLoadFailure(slug: string): void {
    if (!slug) return;
    
    const key = this.getScopedKey(slug);
    const attempts = this.loadAttempts.get(key) || 0;
    
    if (attempts >= this.MAX_LOAD_ATTEMPTS) {
      this.updateTabState(slug, { loading: false, failed: true, loaded: false });
    } else {
      // For retries that aren't caused by timeouts, we'll retry with exponential backoff
      const retryDelay = Math.pow(2, attempts) * 300; // 300ms, 600ms, 1200ms, etc.
      
      // Just remove loading state, but don't mark as failed
      this.updateTabState(slug, { loading: false });
      
      setTimeout(() => {
        // Only retry if it's still not loaded and not already loading
        const currentState = this.getTabStateSubject(slug).getValue();
        if (!currentState.loaded && !currentState.loading) {
          this.loadTabContent(slug);
        }
      }, retryDelay);
    }
  }
  
  // Cleanup all resources associated with a specific slug
  cleanupSlug(slug: string): void {
    if (!slug) return;
    
    const key = this.getScopedKey(slug);
    
    // Clear any timeouts
    if (this.tabLoadTimeouts.has(key)) {
      clearTimeout(this.tabLoadTimeouts.get(key));
      this.tabLoadTimeouts.delete(key);
    }
    
    // Complete the state subject if it exists
    if (this.tabStatesMap.has(key)) {
      this.tabStatesMap.get(key)?.complete();
      this.tabStatesMap.delete(key);
    }
    
    // Clear load attempts
    this.loadAttempts.delete(key);
  }
  
  // Cleanup when the component is destroyed
  destroy(): void {
    // Clear all timeouts
    for (const [key, timeoutId] of this.tabLoadTimeouts.entries()) {
      clearTimeout(timeoutId);
    }
    this.tabLoadTimeouts.clear();
    
    // Clear all state subjects
    for (const [key, subject] of this.tabStatesMap.entries()) {
      subject.complete();
    }
    this.tabStatesMap.clear();
    
    // Clear other maps
    this.loadAttempts.clear();
    this.lastStateUpdateTime.clear();
  }
  
  /**
   * Checks if a tab exists with the given slug prefixed with 'tab/'
   * @param currentSlug The current page slug without 'tab/' prefix
   * @param availableTabs Array of available tab slugs to check against
   * @returns true if a matching tab is found
   */
  hasMatchingTab(currentSlug: string, availableTabs: string[]): boolean {
    if (!currentSlug || !availableTabs || !availableTabs.length) {
      return false;
    }
    
    const tabSlug = `${this.TAB_PREFIX}${currentSlug}`;
    return availableTabs.includes(tabSlug);
  }
  
  /**
   * Gets the matching tab slug for a given page slug
   * @param currentSlug The current page slug without 'tab/' prefix
   * @param availableTabs Array of available tab slugs to check against
   * @returns The matching tab slug or null if no match
   */
  getMatchingTabSlug(currentSlug: string, availableTabs: string[]): string | null {
    if (!currentSlug || !availableTabs || !availableTabs.length) {
      return null;
    }
    
    const tabSlug = `${this.TAB_PREFIX}${currentSlug}`;
    return availableTabs.includes(tabSlug) ? tabSlug : null;
  }
  
  /**
   * Get the default tab to select based on the current slug
   * If the current slug matches a tab (prefixed with 'tab/'), return that tab
   * Otherwise, return the first tab or null if no tabs are available
   * 
   * @param currentSlug The current page slug
   * @param availableTabs Array of available tab slugs
   * @returns The tab slug to select by default
   */
  getDefaultTabSlug(currentSlug: string, availableTabs: string[]): string | null {
    if (!availableTabs || !availableTabs.length) {
      return null;
    }
    
    // If we have a matching tab, return it
    const matchingTab = this.getMatchingTabSlug(currentSlug, availableTabs);
    if (matchingTab) {
      return matchingTab;
    }
    
    // Otherwise return the first tab
    return availableTabs.length > 0 ? availableTabs[0] : null;
  }
} 